// JobForm.tsx
import { useState, useEffect, useMemo } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Tooltip,
  Pagination,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TableSortLabel,
  Stack,
  Collapse,
  useMediaQuery,
} from '@mui/material';
import { Link } from 'react-router-dom';
import { JobService } from '../../../../api';
import { useTheme } from '@mui/material/styles';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import PendingActionsIcon from '@mui/icons-material/PendingActions';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import FilterListIcon from '@mui/icons-material/FilterList';

type SourceType = 'reddit' | 'youtube' | 'google' | 'stackoverflow' | 'x' | 'gk';

interface Job {
  id: number;
  url: string;
  source?: SourceType;
  status: string;
  createdAt: string;
}

const iconMap: Record<SourceType, string> = {
  reddit: '/reddit.png',
  youtube: '/youtube.png',
  google: '/google.png',
  stackoverflow: '/stacko.png',
  x: '/x.webp',
  gk: '/gyik.png',
};

function detectSourceFromUrl(input: string): SourceType | undefined {
  const u = input.toLowerCase();
  if (u.includes('reddit.com/')) return 'reddit';
  if (u.includes('youtube.com/') || u.includes('youtu.be/')) return 'youtube';
  if (u.includes('google.com/maps') || u.includes('maps.app.goo.gl') || u.includes('goo.gl/maps')) return 'google';
  if (u.includes('stackoverflow.com/')) return 'stackoverflow';
  if (u.includes('x.com/') || u.includes('twitter.com/')) return 'x';
  if (u.includes('gyakorikerdesek.hu/')) return 'gk';
  return undefined;
}

type Order = 'asc' | 'desc';
type SortKey = 'source' | 'id' | 'url' | 'status' | 'createdAt';

export default function JobForm() {
  const [url, setUrl] = useState('');
  const [error, setError] = useState('');
  const [jobs, setJobs] = useState<Job[]>([]);
  const [loading, setLoading] = useState(true);
  const [successMessage, setSuccessMessage] = useState('');
  const [page, setPage] = useState(1);
  const rowsPerPage = 10;

  // Szűrők
  const [query, setQuery] = useState('');
  const [sourceFilter, setSourceFilter] = useState<SourceType | 'all'>('all');
  const [statusFilter, setStatusFilter] = useState<string | 'all'>('all');

  // Rendezés
  const [orderBy, setOrderBy] = useState<SortKey>('createdAt');
  const [order, setOrder] = useState<Order>('desc');

  // UI: responsív szűrőpanel
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [filtersOpen, setFiltersOpen] = useState(false);

  useEffect(() => {
    const loadJobs = async () => {
      try {
        const response = await JobService.getApiJobMe();
        setJobs(response.user.jobs || []);
      } catch (err) {
        console.error('Error loading jobs:', err);
        setJobs([]);
      } finally {
        setLoading(false);
      }
    };
    loadJobs();
  }, []);

  const validateUrl = (input: string) => {
    const value = input.trim();
    const patterns = [
      /^https?:\/\/(www\.)?reddit\.com\/r\/[^/]+\/comments\/[A-Za-z0-9_]+/i,
      /^https?:\/\/(www\.)?youtube\.com\/watch\?v=[\w\-]{6,}/i,
      /^https?:\/\/(www\.)?youtu\.be\/[\w\-]{6,}/i,
      /^https?:\/\/(www\.)?youtube\.com\/(shorts|live|embed)\/[\w\-]{6,}/i,
      /^https?:\/\/(www\.)?google\.com\/maps\/.+/i,
      /^https?:\/\/(maps\.app\.goo\.gl|goo\.gl\/maps)\/.+/i,
      /^https?:\/\/(www\.)?stackoverflow\.com\/questions\/\d+/i,
      /^https?:\/\/(www\.)?stackoverflow\.com\/a\/\d+/i,
      /^https?:\/\/(www\.)?(x\.com|twitter\.com)\/[^/]+\/status\/\d+/i,
      /^https?:\/\/(www\.)?gyakorikerdesek\.hu\/.+/i,
    ];
    return patterns.some((re) => re.test(value));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');

    if (!validateUrl(url)) {
      setError('Érvénytelen URL (Támogatott: Reddit, YouTube, Google Maps, Stack Overflow, X/Twitter, GyakoriKérdések)');
      return;
    }

    try {
      const response = await JobService.postApiJobQuery({ url });

      const detected = detectSourceFromUrl(url);
      setJobs((prevJobs) => [
        {
          id: Number(response.job_id),
          url,
          source: detected,
          status: 'Queued',
          createdAt: new Date().toISOString(),
        },
        ...prevJobs,
      ]);

      setSuccessMessage('Sikeres feltöltés!');
      setUrl('');
    } catch (err) {
      setError('A feldolgozás sikertelen. Ellenőrizze az URL-t!');
      console.error('Job submission error:', err);
    }
  };

  // ---- Szűrés + Rendezés + Lapozás ----
  const normalized = (v: unknown) => String(v ?? '').toLowerCase();

  const filteredJobs = useMemo(() => {
    return jobs.filter((j) => {
      const s = (j.source || detectSourceFromUrl(j.url)) as SourceType | undefined;
      const q = query.toLowerCase();
      const matchesQuery =
        !q ||
        normalized(j.id).includes(q) ||
        normalized(j.url).includes(q) ||
        normalized(j.status).includes(q) ||
        normalized(s ?? '').includes(q) ||
        new Date(j.createdAt).toLocaleDateString().toLowerCase().includes(q);

      const matchesSource = sourceFilter === 'all' ? true : (s === sourceFilter);
      const matchesStatus = statusFilter === 'all' ? true : (j.status === statusFilter);

      return matchesQuery && matchesSource && matchesStatus;
    });
  }, [jobs, query, sourceFilter, statusFilter]);

  const comparator = (a: Job, b: Job) => {
    const sA = (a.source || detectSourceFromUrl(a.url)) || '';
    const sB = (b.source || detectSourceFromUrl(b.url)) || '';

    let vA: string | number | Date = '';
    let vB: string | number | Date = '';

    switch (orderBy) {
      case 'id':
        vA = a.id; vB = b.id;
        break;
      case 'url':
        vA = a.url; vB = b.url;
        break;
      case 'source':
        vA = sA; vB = sB;
        break;
      case 'status':
        vA = a.status; vB = b.status;
        break;
      case 'createdAt':
      default:
        vA = new Date(a.createdAt); vB = new Date(b.createdAt);
        break;
    }

    let cmp = 0;
    if (vA instanceof Date && vB instanceof Date) cmp = vA.getTime() - vB.getTime();
    else if (typeof vA === 'number' && typeof vB === 'number') cmp = vA - vB;
    else cmp = String(vA).localeCompare(String(vB));

    return order === 'asc' ? cmp : -cmp;
  };

  const sortedJobs = useMemo(() => [...filteredJobs].sort(comparator), [filteredJobs, order, orderBy]);

  const pagedJobs = useMemo(() => {
    const start = (page - 1) * rowsPerPage;
    return sortedJobs.slice(start, start + rowsPerPage);
  }, [sortedJobs, page]);

  const handleRequestSort = (key: SortKey) => {
    if (orderBy === key) {
      setOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setOrderBy(key);
      setOrder('asc');
    }
  };

  const allStatuses = useMemo(() => Array.from(new Set(jobs.map((j) => j.status))), [jobs]);

  // Statisztikák a SZŰRT listára
  const statusCounts = useMemo(
    () =>
      filteredJobs.reduce((acc, job) => {
        acc[job.status] = (acc[job.status] || 0) + 1;
        return acc;
      }, {} as Record<string, number>),
    [filteredJobs]
  );

  // Közös szűrő UI (desktop + mobil)
  const FiltersRow = (
    <Grid container spacing={2} alignItems="center">
      <Grid item xs={12} md={6}>
        <TextField
          label="Keresés (ID, URL, Forrás, Státusz, Dátum)"
          fullWidth
          value={query}
          onChange={(e) => { setQuery(e.target.value); setPage(1); }}
          size="small"
        />
      </Grid>
      <Grid item xs={6} md={3}>
        <FormControl fullWidth size="small">
          <InputLabel id="src-label">Forrás</InputLabel>
          <Select
            labelId="src-label"
            label="Forrás"
            value={sourceFilter}
            onChange={(e) => { setSourceFilter(e.target.value as any); setPage(1); }}
          >
            <MenuItem value="all">Összes</MenuItem>
            <MenuItem value="reddit">Reddit</MenuItem>
            <MenuItem value="youtube">YouTube</MenuItem>
            <MenuItem value="google">Google Maps</MenuItem>
            <MenuItem value="stackoverflow">Stack Overflow</MenuItem>
            <MenuItem value="x">X / Twitter</MenuItem>
            <MenuItem value="gk">GyakoriKérdések</MenuItem>
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={6} md={3}>
        <FormControl fullWidth size="small">
          <InputLabel id="status-label">Státusz</InputLabel>
          <Select
            labelId="status-label"
            label="Státusz"
            value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value as any); setPage(1); }}
          >
            <MenuItem value="all">Összes</MenuItem>
            {allStatuses.map((s) => (
              <MenuItem key={s} value={s}>{s}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
    </Grid>
  );

  return (
    <Box sx={{ mx: 'auto', mt: 4, p: 3 }}>
      <Typography variant="h4" gutterBottom>Új elemzés</Typography>

      <Box sx={{ mx: 'auto', mt: 4, p: 3 }}>
        <Box component="form" onSubmit={handleSubmit}>
          <TextField
            label="Elemzendő URL"
            fullWidth
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            error={!!error}
            helperText={error}
          />

          {successMessage && (
            <Typography color="success.main" sx={{ mt: 1 }}>
              {successMessage}
            </Typography>
          )}

          <Button type="submit" variant="contained" sx={{ mt: 2 }}>
            Küldés
          </Button>
        </Box>
      </Box>

      {/* Cím + szűrők (responsív) */}
      <Box sx={{ mt: 2 }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          alignItems={{ xs: 'flex-start', md: 'center' }}
          justifyContent="space-between"
          spacing={2}
          sx={{ mb: 1 }}
        >
          <Typography variant="h5">Korábbi elemzéseim</Typography>

          {/* Desktop: szűrők a cím mellett jobbra */}
          <Box sx={{ display: { xs: 'none', md: 'block' }, minWidth: '55%' }}>
            {FiltersRow}
          </Box>

          {/* Mobil: „Szűrők” gomb a cím alatt */}
          <Box sx={{ display: { xs: 'block', md: 'none' }, width: '100%' }}>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<FilterListIcon />}
              onClick={() => setFiltersOpen((v) => !v)}
            >
              Szűrők
            </Button>
          </Box>
        </Stack>

        {/* Mobil: lenyíló szűrőpanel */}
        <Collapse in={filtersOpen} timeout="auto" unmountOnExit sx={{ display: { xs: 'block', md: 'none' } }}>
          <Paper variant="outlined" sx={{ p: 2, mt: 1 }}>
            {FiltersRow}
          </Paper>
        </Collapse>
      </Box>

      {/* Lista */}
      <Box sx={{ mt: 2 }}>
        {loading ? (
          <CircularProgress />
        ) : (
          sortedJobs.length > 0 ? (
            <>
              {/* Desktop táblázat */}
              <Box sx={{ display: { xs: 'none', md: 'block' } }}>
                <TableContainer
                  component={Paper}
                  sx={{
                    maxWidth: '100%',
                    overflowX: 'auto',
                    '& .MuiTableCell-root': { whiteSpace: 'nowrap' }
                  }}
                >
                  <Table sx={{ width: '100%' }} aria-label="jobs table">
                    <TableHead>
                      <TableRow sx={{ backgroundColor: theme.palette.action.hover }}>
                        <TableCell sx={{ width: 56 }}>
                          <TableSortLabel
                            active={orderBy === 'source'}
                            direction={orderBy === 'source' ? order : 'asc'}
                            onClick={() => handleRequestSort('source')}
                          >
                            Forrás
                          </TableSortLabel>
                        </TableCell>
                        <TableCell>
                          <TableSortLabel
                            active={orderBy === 'id'}
                            direction={orderBy === 'id' ? order : 'asc'}
                            onClick={() => handleRequestSort('id')}
                          >
                            ID
                          </TableSortLabel>
                        </TableCell>
                        <TableCell>
                          <TableSortLabel
                            active={orderBy === 'url'}
                            direction={orderBy === 'url' ? order : 'asc'}
                            onClick={() => handleRequestSort('url')}
                          >
                            URL
                          </TableSortLabel>
                        </TableCell>
                        <TableCell>
                          <TableSortLabel
                            active={orderBy === 'status'}
                            direction={orderBy === 'status' ? order : 'asc'}
                            onClick={() => handleRequestSort('status')}
                          >
                            Státusz
                          </TableSortLabel>
                        </TableCell>
                        <TableCell>
                          <TableSortLabel
                            active={orderBy === 'createdAt'}
                            direction={orderBy === 'createdAt' ? order : 'desc'}
                            onClick={() => handleRequestSort('createdAt')}
                          >
                            Létrehozva
                          </TableSortLabel>
                        </TableCell>
                        <TableCell>Műveletek</TableCell>
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      {pagedJobs.map((job) => {
                        const source = (job.source || detectSourceFromUrl(job.url)) as SourceType | undefined;
                        const icon = source ? iconMap[source] : undefined;
                        return (
                          <TableRow key={job.id}>
                            <TableCell>
                              {icon && (
                                <img
                                  src={icon}
                                  alt={source}
                                  style={{ width: 24, height: 24, display: 'block' }}
                                />
                              )}
                            </TableCell>
                            <TableCell>{job.id}</TableCell>
                            <TableCell>
                              <Tooltip title={job.url}>
                                <Typography
                                  variant="body2"
                                  component="a"
                                  href={job.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  sx={{
                                    display: 'inline-block',
                                    maxWidth: '360px',
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    color: 'primary.main',
                                  }}
                                >
                                  {job.url}
                                </Typography>
                              </Tooltip>
                            </TableCell>
                            <TableCell>
                              <Box
                                sx={{
                                  display: 'inline-block',
                                  px: 1.5,
                                  py: 0.5,
                                  borderRadius: 2,
                                  fontWeight: 500,
                                  fontSize: '0.875rem',
                                  textAlign: 'center',
                                  minWidth: '80px',
                                  color: (() => {
                                    switch (job.status) {
                                      case 'Queued':
                                        return theme.palette.warning.contrastText;
                                      case 'Completed':
                                        return theme.palette.success.contrastText;
                                      case 'Failed':
                                        return theme.palette.error.contrastText;
                                      default:
                                        return theme.palette.text.primary;
                                    }
                                  })(),
                                  backgroundColor: (() => {
                                    switch (job.status) {
                                      case 'Queued':
                                        return theme.palette.warning.light;
                                      case 'Completed':
                                        return theme.palette.success.light;
                                      case 'Failed':
                                        return theme.palette.error.light;
                                      default:
                                        return theme.palette.grey[400];
                                    }
                                  })()
                                }}
                              >
                                {job.status}
                              </Box>
                            </TableCell>
                            <TableCell>{new Date(job.createdAt).toLocaleDateString()}</TableCell>
                            <TableCell>
                              <Button
                                component={Link}
                                to={`/job/${job.id}/comments`}
                                variant="contained"
                                color="primary"
                                size="small"
                                sx={{ textTransform: 'none', px: 2, py: 1 }}
                              >
                                Kommentek
                              </Button>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>

              {/* Mobil kártyák */}
              <Box sx={{ display: { xs: 'flex', md: 'none' }, flexDirection: 'column', gap: 2 }}>
                {pagedJobs.map((job) => {
                  const source = (job.source || detectSourceFromUrl(job.url)) as SourceType | undefined;
                  const icon = source ? iconMap[source] : undefined;
                  return (
                    <Paper key={job.id} sx={{ p: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        {icon && <img src={icon} alt={source} style={{ width: 20, height: 20 }} />}
                        <Typography variant="subtitle2">ID: {job.id}</Typography>
                      </Box>
                      <Typography
                        variant="body2"
                        component="a"
                        href={job.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{ wordBreak: 'break-all', display: 'block', color: 'primary.main', mb: 1 }}
                      >
                        {job.url}
                      </Typography>
                      <Typography variant="body2">Státusz: {job.status}</Typography>
                      <Typography variant="body2">Létrehozva: {new Date(job.createdAt).toLocaleDateString()}</Typography>
                      <Button
                        component={Link}
                        to={`/job/${job.id}/comments`}
                        variant="contained"
                        color="primary"
                        size="small"
                        sx={{ mt: 1 }}
                      >
                        Kommentek
                      </Button>
                    </Paper>
                  );
                })}
              </Box>

              <Pagination
                count={Math.ceil(sortedJobs.length / rowsPerPage)}
                page={page}
                onChange={(_, value) => setPage(value)}
                color="primary"
                size="small"
                sx={{ mt: 2, mb: 5, display: 'flex', justifyContent: 'center' }}
              />

              {/* Státusz kártyák (szűrt nézethez) */}
              <Box sx={{ mt: 2 }}>
                <Grid container spacing={2}>
                  <Grid item xs={6} md={3}>
                    <Paper
                      variant="outlined"
                      sx={{
                        p: 2,
                        textAlign: 'center',
                        borderColor: theme.palette.success.main,
                        transition: 'background-color 0.3s',
                        '&:hover': { backgroundColor: theme.palette.success.light },
                      }}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1, mb: 1 }}>
                        <CheckCircleOutlineIcon sx={{ color: 'success.main', fontSize: 20 }} />
                        <Typography variant="subtitle2" color="success.main">Completed</Typography>
                      </Box>
                      <Typography variant="h6">{statusCounts['Completed'] || 0}</Typography>
                    </Paper>
                  </Grid>

                  <Grid item xs={6} md={3}>
                    <Paper
                      variant="outlined"
                      sx={{
                        p: 2,
                        textAlign: 'center',
                        borderColor: theme.palette.warning.main,
                        transition: 'background-color 0.3s',
                        '&:hover': { backgroundColor: theme.palette.warning.light },
                      }}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1, mb: 1 }}>
                        <HourglassEmptyIcon sx={{ color: 'warning.main', fontSize: 20 }} />
                        <Typography variant="subtitle2" color="warning.main">Queued</Typography>
                      </Box>
                      <Typography variant="h6">{statusCounts['Queued'] || 0}</Typography>
                    </Paper>
                  </Grid>

                  <Grid item xs={6} md={3}>
                    <Paper
                      variant="outlined"
                      sx={{
                        p: 2,
                        textAlign: 'center',
                        borderColor: theme.palette.info.main,
                        transition: 'background-color 0.3s',
                        '&:hover': { backgroundColor: theme.palette.info.light },
                      }}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1, mb: 1 }}>
                        <PendingActionsIcon sx={{ color: 'info.main', fontSize: 20 }} />
                        <Typography variant="subtitle2" color="info.main">Pending</Typography>
                      </Box>
                      <Typography variant="h6">{statusCounts['Pending'] || 0}</Typography>
                    </Paper>
                  </Grid>

                  <Grid item xs={6} md={3}>
                    <Paper
                      variant="outlined"
                      sx={{
                        p: 2,
                        textAlign: 'center',
                        borderColor: theme.palette.error.main,
                        transition: 'background-color 0.3s',
                        '&:hover': { backgroundColor: theme.palette.error.light },
                      }}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1, mb: 1 }}>
                        <ErrorOutlineIcon sx={{ color: 'error.main', fontSize: 20 }} />
                        <Typography variant="subtitle2" color="error.main">Failed</Typography>
                      </Box>
                      <Typography variant="h6">{statusCounts['Failed'] || 0}</Typography>
                    </Paper>
                  </Grid>
                </Grid>
              </Box>
            </>
          ) : (
            <Typography variant="body1">Nincsenek találatok a megadott szűrőkre</Typography>
          )
        )}
      </Box>
    </Box>
  );
}
